/*
 * Dot pager for the curated-collections band.
 *
 * Dawn's <slider-component> (assets/global.js) already owns the scrolling, the
 * arrow enable/disable states and the ResizeObserver re-measure, and it emits a
 * `slideChanged` event — but the only dot support in that file lives on
 * SlideshowComponent, which also loops and autoplays. Rather than adopt that,
 * this reads the slider's own measurements and renders the pager beside it.
 *
 * The element renders nothing until it knows there is more than one page, so if
 * this file fails to load the band is still a working carousel: arrows, wheel,
 * drag and swipe all come from CSS and the base component.
 */
class MmCollectionsCarouselDots extends HTMLElement {
  connectedCallback() {
    // The base component may be defined after this one (both are deferred, and
    // section rendering in the theme editor re-runs this at arbitrary times).
    // Waiting on the definition also guarantees the instance below has been
    // upgraded, so its initPages() measurements exist by the time we read them.
    customElements.whenDefined('slider-component').then(() => this.init());
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  init() {
    this.sliderComponent = this.closest('slider-component');
    this.slider = this.sliderComponent?.querySelector('[id^="Slider-"]');
    if (!this.slider) return;

    this.render();

    this.sliderComponent.addEventListener('slideChanged', () => this.setActiveDot());
    this.slider.addEventListener('scroll', () => this.setActiveDot());

    // Page count is a function of the track's width, so it has to be recomputed
    // on resize. Observing fires once immediately, which is harmless — render()
    // bails out when the count is unchanged.
    this.resizeObserver = new ResizeObserver(() => this.render());
    this.resizeObserver.observe(this.slider);
  }

  /*
   * Deliberately *not* slider-component's own `totalPages`. That is derived
   * from `slidesPerPage = floor((clientWidth - firstOffsetLeft) / itemOffset)`,
   * which under-counts by one whenever the tiles are gapped: four tiles sized
   * to `(100% - 3*gap) / 4` occupy exactly the track, so four item offsets add
   * up to `100% + gap` — wider than clientWidth — and the floor lands on 3.
   *
   * Measuring the scrollable distance instead sidesteps that entirely: the
   * arrows advance one tile per click, so the number of resting positions is
   * however many whole tiles the track can travel, plus the starting one.
   */
  get pageCount() {
    const offset = this.sliderComponent.sliderItemOffset;
    if (!offset) return 0;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    if (maxScroll <= 1) return 1;
    return Math.round(maxScroll / offset) + 1;
  }

  get activeIndex() {
    const offset = this.sliderComponent.sliderItemOffset;
    if (!offset) return 0;
    const index = Math.round(this.slider.scrollLeft / offset);
    return Math.min(Math.max(index, 0), this.dots.length - 1);
  }

  render() {
    const count = this.pageCount;
    if (count === this.renderedCount) return;
    this.renderedCount = count;

    this.innerHTML = '';
    // One page means nothing to page through; an unusable row of one dot is
    // worse than no row at all, and hiding it also drops its bottom padding.
    this.toggleAttribute('hidden', count < 2);
    if (count < 2) {
      this.dots = [];
      return;
    }

    const fragment = document.createDocumentFragment();
    for (let index = 0; index < count; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mm-collections__dot';
      dot.setAttribute('aria-label', `${this.dataset.slideLabel || 'Slide'} ${index + 1}`);
      dot.setAttribute('aria-controls', this.slider.id);
      dot.addEventListener('click', () => {
        this.sliderComponent.setSlidePosition(index * this.sliderComponent.sliderItemOffset);
      });
      fragment.appendChild(dot);
    }
    this.appendChild(fragment);
    this.dots = Array.from(this.children);
    this.setActiveDot();
  }

  setActiveDot() {
    if (!this.dots?.length) return;
    const active = this.activeIndex;
    this.dots.forEach((dot, index) => {
      // aria-current is both the a11y signal and the CSS hook, so the two can
      // never drift apart.
      dot.setAttribute('aria-current', index === active ? 'true' : 'false');
    });
  }
}

customElements.define('mm-collections-dots', MmCollectionsCarouselDots);
